import { useContext, createContext, useState, useEffect } from "react";
import { login } from "../services/authenticationService";
import { setForceLogoutSuppressed } from "../services/apiService";
import { useCart } from "../contexts/cartcontext";
import { removeGuestCookie } from "../utils/guestHelper";
import {
  clearAppStorage,
  getAuthTokens,
  getLoginTimestamp,
  getStoredUser,
  setAuthSession,
  setStoredUser,
} from "../utils/authStorage";

import { GUEST_CART_STORAGE_KEY } from "../constants/guestConstants";

const SESSION_TIMEOUT = 12 * 60 * 60 * 1000; 

const AuthContext = createContext();
const AuthProvider = ({ children }) => {
  const initialTokens = getAuthTokens();
  const [user, setUser] = useState(getStoredUser());
  const [token, setToken] = useState(initialTokens.bearerToken || "");
  const [accessToken, setAccessToken] = useState(initialTokens.accessToken || "");
  const { fetchCart } = useCart(); 
  

    const checkSessionExpiry = () => {
      const loginTimestamp = getLoginTimestamp();
      if (loginTimestamp) {
        const currentTime = new Date().getTime();
        if (currentTime - loginTimestamp > SESSION_TIMEOUT) {
          logOut();
        }
      }
    };

  const loginAction = async (data) => {
    try {
      // Get guest UID if exists
      const guestUID = require('../utils/guestHelper').getGuestCookie();
      
      // Add guestUserId to login payload if guest cart exists
      const loginPayload = guestUID ? { ...data, guestUserId: guestUID } : data;
      
      const res = await login(loginPayload)
      if (res) {
        const authorizationToken = res?.data?.authorizationToken;
        const accessToken = res?.data?.accessToken;
        const userData = res?.data;
        setAuthSession(userData);
        setToken(authorizationToken);
        setAccessToken(accessToken);
        setUser(userData);
        
        // Sync guest cart if exists (this will fetch cart with guestUserId)
        await syncGuestCart();
        
        // Don't call fetchCart() here - syncGuestCart already does it
        // Calling it again would fetch without guestUserId and overwrite with empty data
        
        return res
      }
      throw new Error(res.message);
    } catch (error) {
      // Check error message for unverified email
      const errorMessage = error?.response?.data?.message || error?.message || "";
      
      if (errorMessage.toLowerCase().includes("not verified") || errorMessage.toLowerCase().includes("unverified")) {
        // Backend already sent OTP, just throw error for component to show modal
        const unverifiedError = new Error(errorMessage);
        unverifiedError.type = 'EMAIL_NOT_VERIFIED';
        unverifiedError.email = data?.email;
        throw unverifiedError;
      } else {
        console.error("Login failed", error);
        throw error;
      }
    }
  };

  const syncGuestCart = async () => {
    // A 403 here (e.g. a stale guestUserId) shouldn't force-logout a user who just logged in.
    setForceLogoutSuppressed(true);
    try {
      // Get guest UID from cookie BEFORE removing it
      const guestUID = require('../utils/guestHelper').getGuestCookie();

      if (guestUID) {
        // Fetch cart with guestUserId so backend can merge/return the correct cart
        // Pass guestUID explicitly because user is now authenticated (token exists)
        await fetchCart(guestUID);

        // Clear guest cart from localStorage
        localStorage.removeItem(GUEST_CART_STORAGE_KEY);

        // Remove guest cookie after successful sync
        removeGuestCookie();
      } else {
        // No guest cart to sync, just fetch user's cart
        await fetchCart();
      }
    } catch (error) {
      console.error("Error syncing guest cart:", error);
    } finally {
      setForceLogoutSuppressed(false);
    }
  };

  const setAuthState = async (authData) => {
    // Update auth state with new user data (used after signup)
    setAuthSession(authData);
    setToken(authData.authorizationToken);
    setAccessToken(authData.accessToken);
    setUser(authData);
    
    // Sync guest cart after authentication
    await syncGuestCart();
  };

  const updateUser = (updatedFields) => {
    setUser((prevUser) => {
      const mergedUser = { ...prevUser, ...updatedFields };
      setStoredUser(mergedUser);
      return mergedUser;
    });
  };

  const logOut = () => {
      // Clear tokens from state
  setToken("");
  setAccessToken("");
  setUser({});
  clearAppStorage();
  window.location.href = "/"
  };

  useEffect(() => {
    checkSessionExpiry();
  }, []);

  return (
    <AuthContext.Provider value={{ token, user, accessToken, loginAction, logOut, setAuthState, updateUser }}>
      {children}
    </AuthContext.Provider>
  );

};
export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthProvider;



